import { FC, useEffect, useRef } from 'react';

import { CheckCircleIcon, ClockIcon, ExclamationIcon } from '@heroicons/react/outline';

import { ContactEmailLink } from '../../components/ContactEmailLink';
import CouponProductCard from './CouponProductCard';
import SendScheduleCard from './SendScheduleCard';

interface SubmitSuccessViewProps {
  redeemSubmitMeta: { submittedAt: Date; expectedSendAt: Date } | null;
  imageUrl: string;
  itemName: string;
  couponCode: string;
  brandName: string | null;
  selectedUnit: { name: string; image?: { url?: string } } | null;
  validPeriod?: string;
}

const SubmitSuccessView: FC<SubmitSuccessViewProps> = ({
  redeemSubmitMeta,
  imageUrl,
  itemName,
  couponCode,
  brandName,
  selectedUnit,
  validPeriod,
}) => {
  const scheduleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const timer = setTimeout(() => {
      scheduleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 600);

    return () => clearTimeout(timer);
  }, []);

  return (
  <div className="min-h-screen bg-gradient-to-b from-stone-100 to-white px-4 py-8">
    <div className="mx-auto max-w-md">
      <div className="rounded-xl border border-stone-200 bg-stone-50 p-6 text-center">
        <CheckCircleIcon className="mx-auto h-10 w-10 text-stone-500" />
        <h2 className="mt-3 text-lg font-medium text-stone-800">신청이 완료되었습니다</h2>
        <p className="mt-1 whitespace-pre-line text-sm text-stone-500">
          {'입력하신 번호로 곧 전송됩니다.\n잠시만 기다려 주세요.\n\n영업일 기준 10시, 14시, 18시에 발송됩니다.\n발송까지 30분~1시간 소요될 수 있습니다.\n(주말 및 공휴일 제외, 익영업일 기준)'}
        </p>
      </div>

      {redeemSubmitMeta && (
        <div ref={scheduleRef} className="animate-highlight-pulse">
          <SendScheduleCard
            requestedAt={redeemSubmitMeta.submittedAt}
            expectedSendAt={redeemSubmitMeta.expectedSendAt}
          />
        </div>
      )}

      <div className="mt-4 space-y-3">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-2.5">
            <ClockIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
            <div>
              <p className="text-sm font-bold text-amber-900">
                기프티콘 유효기간 안내
              </p>
              <p className="mt-1.5 break-keep text-sm leading-relaxed text-amber-800">
                발송된 기프티콘은 발송일로부터{' '}
                <span className="font-bold">{validPeriod || '31일'}</span> 내에
                사용하셔야 합니다.
              </p>
              <p className="mt-1 text-xs text-amber-700">
                기한 내 미사용 시 소멸되며, 복구가 불가합니다.
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-2.5">
            <ExclamationIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
            <div>
              <p className="text-sm font-bold text-red-800">
                카카오톡 &quot;선물함&quot;이 아닙니다
              </p>
              <p className="mt-1.5 break-keep text-sm leading-relaxed text-red-700">
                기프티콘은{' '}
                <span className="font-bold">
                  문자(SMS) 또는 카카오톡 알림톡
                </span>
                으로 발송됩니다.
              </p>
              <p className="mt-1 break-keep text-xs leading-relaxed text-red-600">
                카카오톡 선물함이 아닌, 받으신 문자 또는 알림톡에서 기프티콘
                이미지를 확인하여 매장에서 제시해 주세요.
              </p>
            </div>
          </div>
        </div>
      </div>

      <CouponProductCard
        imageUrl={imageUrl}
        itemName={itemName}
        couponCode={couponCode}
        brandName={brandName}
        selectedUnit={selectedUnit}
      />

      <ContactEmailLink />
    </div>
  </div>
  );
};

export default SubmitSuccessView;
